/**
 * FAIR Compass Cost Configuration Panel
 * Ported from Pistoia Alliance FAIR-ValuePath-Calculator
 *
 * Main component for configuring project costs
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BRANDING } from '../../config/branding';
import {
  ProjectCosts,
  CostSummary,
  PersonnelRole,
  LineItem,
  CapExItem,
  OpExItem,
  ComplianceItem,
  VendorItem,
} from './costTypes';
import {
  createDefaultProjectCosts,
  recalculateCosts,
  extractCostSummary,
  formatCurrency,
  generateId,
  adjustCostsForOrganization,
  OrgSize,
  Industry,
} from './costModel';
import './CostConfiguration.css';

// ============================================================================
// Props Interface
// ============================================================================

interface CostConfigurationPanelProps {
  journeyId: string;
  initialCosts?: ProjectCosts;
  orgSize?: OrgSize;
  industry?: Industry;
  headcount?: number;
  onCostChange?: (summary: CostSummary) => void;
  onSave?: (costs: ProjectCosts) => void;
  onClose?: () => void;
}

// ============================================================================
// Main Component
// ============================================================================

export const CostConfigurationPanel: React.FC<CostConfigurationPanelProps> = ({
  journeyId: _journeyId,
  initialCosts,
  orgSize = 'medium',
  industry = 'pharma',
  headcount = 50,
  onCostChange,
  onSave,
  onClose,
}) => {
  // State
  const [costs, setCosts] = useState<ProjectCosts>(() => {
    if (initialCosts) {
      return initialCosts;
    }
    const defaultCosts = createDefaultProjectCosts();
    adjustCostsForOrganization(defaultCosts, { size: orgSize, industry, headcount });
    return defaultCosts;
  });

  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['personnel', 'vendor'])
  );

  const [hasChanges, setHasChanges] = useState(false);

  /*
   * Recalculate and notify on COST changes only (S4h). `onCostChange` used to
   * be an effect dependency, and the Action Plan panel passed an inline arrow
   * — so every notification re-rendered the parent, which re-created the
   * callback, which re-fired this effect: the "Maximum update depth exceeded"
   * crash on Configure Costs. The callback lives in a ref (always current,
   * never a dependency), and the effect keys on `costs` alone.
   */
  const onCostChangeRef = useRef(onCostChange);
  useEffect(() => {
    onCostChangeRef.current = onCostChange;
  });
  useEffect(() => {
    recalculateCosts(costs);
    onCostChangeRef.current?.(extractCostSummary(costs));
  }, [costs]);

  // Toggle section expansion
  const toggleSection = useCallback((sectionId: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  }, []);

  // Toggle category inclusion
  const toggleCategory = useCallback((category: keyof ProjectCosts) => {
    setCosts(prev => {
      const updated = { ...prev };
      const cat = updated[category] as { included: boolean };
      if ('included' in cat) {
        cat.included = !cat.included;
      }
      recalculateCosts(updated);
      setHasChanges(true);
      return updated;
    });
  }, []);

  // Update a field in a category
  const updateField = useCallback((
    category: keyof ProjectCosts,
    field: string,
    value: unknown
  ) => {
    setCosts(prev => {
      const updated = { ...prev };
      const cat = updated[category] as unknown as Record<string, unknown>;
      cat[field] = value;
      recalculateCosts(updated);
      setHasChanges(true);
      return updated;
    });
  }, []);

  // Handle save
  const handleSave = useCallback(() => {
    if (onSave) {
      onSave(costs);
    }
    setHasChanges(false);
  }, [costs, onSave]);

  // Handle reset
  const handleReset = useCallback(() => {
    const confirmed = window.confirm(
      'Reset all costs to defaults? This cannot be undone.'
    );
    if (confirmed) {
      const defaultCosts = createDefaultProjectCosts();
      adjustCostsForOrganization(defaultCosts, { size: orgSize, industry, headcount });
      setCosts(defaultCosts);
      setHasChanges(true);
    }
  }, [orgSize, industry, headcount]);

  // Render
  return (
    <div className="cost-configuration-panel">
      {/* Header */}
      <div className="cost-config-header">
        <div className="cost-config-title-section">
          <h2 className="cost-config-title">Project Cost Configuration</h2>
          <p className="cost-config-subtitle">
            Configure implementation costs for accurate ROI calculations
          </p>
        </div>
        <div className="cost-config-actions">
          <button
            className="btn btn-secondary"
            onClick={handleReset}
            title="Reset to defaults"
          >
            Reset
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={!hasChanges}
          >
            Save
          </button>
          {onClose && (
            <button
              className="btn btn-ghost"
              onClick={onClose}
              title="Close"
            >
              &times;
            </button>
          )}
        </div>
      </div>

      {/* Summary Banner */}
      <CostSummaryBanner costs={costs} />

      {/* Cost Sections */}
      <div className="cost-sections">
        <CostSection
          id="personnel"
          title="Personnel"
          icon="👥"
          total={costs.personnel.total}
          included={costs.personnel.included}
          expanded={expandedSections.has('personnel')}
          onToggle={() => toggleSection('personnel')}
          onToggleIncluded={() => toggleCategory('personnel')}
        >
          <PersonnelSection
            data={costs.personnel}
            onChange={(roles) => updateField('personnel', 'roles', roles)}
          />
        </CostSection>

        <CostSection
          id="technology"
          title="Technology"
          icon="💻"
          total={costs.technology.total}
          included={costs.technology.included}
          expanded={expandedSections.has('technology')}
          onToggle={() => toggleSection('technology')}
          onToggleIncluded={() => toggleCategory('technology')}
        >
          <TechnologySection
            data={costs.technology}
            onChangeCapex={(items) => updateField('technology', 'capex', items)}
            onChangeOpex={(items) => updateField('technology', 'opex', items)}
          />
        </CostSection>

        <CostSection
          id="training"
          title="Training & Change Management"
          icon="📚"
          total={costs.training.total}
          included={costs.training.included}
          expanded={expandedSections.has('training')}
          onToggle={() => toggleSection('training')}
          onToggleIncluded={() => toggleCategory('training')}
        >
          <LineItemSection
            items={costs.training.items}
            onChange={(items) => updateField('training', 'items', items)}
          />
        </CostSection>

        <CostSection
          id="vendor"
          title={`Vendor (${BRANDING.productName})`}
          icon="🏢"
          total={costs.vendor.total}
          included={costs.vendor.included}
          expanded={expandedSections.has('vendor')}
          onToggle={() => toggleSection('vendor')}
          onToggleIncluded={() => toggleCategory('vendor')}
        >
          <VendorSection
            items={costs.vendor.items}
            onChange={(items) => updateField('vendor', 'items', items)}
          />
        </CostSection>

        <CostSection
          id="risk"
          title="Risk & Contingency"
          icon="⚠️"
          total={costs.risk.total}
          included={costs.risk.included}
          expanded={expandedSections.has('risk')}
          onToggle={() => toggleSection('risk')}
          onToggleIncluded={() => toggleCategory('risk')}
        >
          <RiskSection
            data={costs.risk}
            onChange={(reserves) => updateField('risk', 'reserves', reserves)}
          />
        </CostSection>

        {/* Advanced sections (collapsed by default) */}
        <div className="advanced-sections">
          <h3 className="advanced-sections-title">Advanced Cost Categories</h3>

          <CostSection
            id="migration"
            title="Data Migration & Remediation"
            icon="🔄"
            total={costs.migration.total}
            included={costs.migration.included}
            expanded={expandedSections.has('migration')}
            onToggle={() => toggleSection('migration')}
            onToggleIncluded={() => toggleCategory('migration')}
          >
            <LineItemSection
              items={costs.migration.items}
              onChange={(items) => updateField('migration', 'items', items)}
            />
          </CostSection>

          <CostSection
            id="compliance"
            title="Compliance (Pharma)"
            icon="📋"
            total={costs.compliance.total}
            included={costs.compliance.included}
            expanded={expandedSections.has('compliance')}
            onToggle={() => toggleSection('compliance')}
            onToggleIncluded={() => toggleCategory('compliance')}
          >
            <ComplianceSection
              items={costs.compliance.items}
              onChange={(items) => updateField('compliance', 'items', items)}
            />
          </CostSection>

          <CostSection
            id="disruption"
            title="Operational Disruption"
            icon="⏸️"
            total={costs.disruption.total}
            included={costs.disruption.included}
            expanded={expandedSections.has('disruption')}
            onToggle={() => toggleSection('disruption')}
            onToggleIncluded={() => toggleCategory('disruption')}
          >
            <LineItemSection
              items={costs.disruption.items}
              onChange={(items) => updateField('disruption', 'items', items)}
            />
          </CostSection>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Cost Summary Banner
// ============================================================================

interface CostSummaryBannerProps {
  costs: ProjectCosts;
}

const CostSummaryBanner: React.FC<CostSummaryBannerProps> = ({ costs }) => {
  const categories = [
    { label: 'Personnel', value: costs.personnel.total, included: costs.personnel.included },
    { label: 'Technology', value: costs.technology.total, included: costs.technology.included },
    { label: 'Training', value: costs.training.total, included: costs.training.included },
    { label: 'Vendor', value: costs.vendor.total, included: costs.vendor.included },
    { label: 'Risk', value: costs.risk.total, included: costs.risk.included },
  ];

  return (
    <div className="cost-summary-banner">
      <div className="cost-summary-main">
        <div className="cost-summary-label">Total Project Cost</div>
        <div className="cost-summary-value">{formatCurrency(costs.total)}</div>
      </div>
      <div className="cost-summary-breakdown">
        {categories.filter(c => c.included).map(cat => (
          <div key={cat.label} className="cost-summary-item">
            <span className="cost-summary-item-label">{cat.label}</span>
            <span className="cost-summary-item-value">{formatCurrency(cat.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// Cost Section Wrapper (Accordion)
// ============================================================================

interface CostSectionProps {
  id: string;
  title: string;
  icon: string;
  total: number;
  included: boolean;
  expanded: boolean;
  onToggle: () => void;
  onToggleIncluded: () => void;
  children: React.ReactNode;
}

const CostSection: React.FC<CostSectionProps> = ({
  id: _id,
  title,
  icon,
  total,
  included,
  expanded,
  onToggle,
  onToggleIncluded,
  children,
}) => {
  return (
    <div className={`cost-section ${expanded ? 'expanded' : ''} ${!included ? 'disabled' : ''}`}>
      <div className="cost-section-header" onClick={onToggle}>
        <div className="cost-section-left">
          <span className="cost-section-icon">{icon}</span>
          <span className="cost-section-title">{title}</span>
        </div>
        <div className="cost-section-right">
          <span className="cost-section-total">
            {included ? formatCurrency(total) : 'Excluded'}
          </span>
          <label className="cost-section-toggle" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={included}
              onChange={onToggleIncluded}
            />
            <span className="toggle-slider"></span>
          </label>
          <span className={`cost-section-chevron ${expanded ? 'rotated' : ''}`}>
            ▼
          </span>
        </div>
      </div>
      {expanded && included && (
        <div className="cost-section-content">
          {children}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Personnel Section
// ============================================================================

interface PersonnelSectionProps {
  data: { roles: PersonnelRole[] };
  onChange: (roles: PersonnelRole[]) => void;
}

const PersonnelSection: React.FC<PersonnelSectionProps> = ({ data, onChange }) => {
  const addRole = () => {
    const newRole: PersonnelRole = {
      id: generateId(),
      role: 'New Role',
      fte: 0.5,
      duration_months: 12,
      loaded_rate: 80,
      opportunity_cost: 0,
      notes: '',
    };
    onChange([...data.roles, newRole]);
  };

  const updateRole = (id: string, field: keyof PersonnelRole, value: unknown) => {
    onChange(data.roles.map(role =>
      role.id === id ? { ...role, [field]: value } : role
    ));
  };

  const deleteRole = (id: string) => {
    if (data.roles.length > 1) {
      onChange(data.roles.filter(role => role.id !== id));
    }
  };

  return (
    <div className="personnel-section">
      <table className="cost-table">
        <thead>
          <tr>
            <th>Role</th>
            <th>FTE</th>
            <th>Duration</th>
            <th>Rate ($/hr)</th>
            <th>Total</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {data.roles.map(role => {
            const totalHours = role.fte * role.duration_months * 160;
            const roleTotal = totalHours * role.loaded_rate;
            return (
              <tr key={role.id}>
                <td>
                  <input
                    type="text"
                    value={role.role}
                    onChange={(e) => updateRole(role.id, 'role', e.target.value)}
                    className="input-text"
                  />
                </td>
                <td>
                  <input
                    type="number"
                    value={role.fte}
                    min={0.1}
                    max={10}
                    step={0.1}
                    onChange={(e) => updateRole(role.id, 'fte', parseFloat(e.target.value) || 0)}
                    className="input-number"
                  />
                </td>
                <td>
                  <input
                    type="number"
                    value={role.duration_months}
                    min={1}
                    max={60}
                    onChange={(e) => updateRole(role.id, 'duration_months', parseInt(e.target.value) || 1)}
                    className="input-number"
                  />
                  <span className="input-suffix">mo</span>
                </td>
                <td>
                  <input
                    type="number"
                    value={role.loaded_rate}
                    min={0}
                    onChange={(e) => updateRole(role.id, 'loaded_rate', parseFloat(e.target.value) || 0)}
                    className="input-number"
                  />
                </td>
                <td className="total-cell">{formatCurrency(roleTotal)}</td>
                <td>
                  <button
                    className="btn-icon btn-delete"
                    onClick={() => deleteRole(role.id)}
                    title="Delete role"
                    disabled={data.roles.length <= 1}
                  >
                    ×
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <button className="btn btn-secondary btn-add" onClick={addRole}>
        + Add Role
      </button>
    </div>
  );
};

// ============================================================================
// Technology Section
// ============================================================================

interface TechnologySectionProps {
  data: { capex: CapExItem[]; opex: OpExItem[] };
  onChangeCapex: (items: CapExItem[]) => void;
  onChangeOpex: (items: OpExItem[]) => void;
}

const TechnologySection: React.FC<TechnologySectionProps> = ({
  data,
  onChangeCapex,
  onChangeOpex,
}) => {
  return (
    <div className="technology-section">
      <h4>Capital Expenditure (CapEx)</h4>
      <table className="cost-table">
        <thead>
          <tr>
            <th>Item</th>
            <th>Cost</th>
            <th>Depreciation</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {data.capex.map(item => (
            <tr key={item.id}>
              <td>
                <input
                  type="text"
                  value={item.item}
                  onChange={(e) => {
                    const updated = data.capex.map(i =>
                      i.id === item.id ? { ...i, item: e.target.value } : i
                    );
                    onChangeCapex(updated);
                  }}
                  className="input-text"
                />
              </td>
              <td>
                <input
                  type="number"
                  value={item.cost}
                  onChange={(e) => {
                    const updated = data.capex.map(i =>
                      i.id === item.id ? { ...i, cost: parseFloat(e.target.value) || 0 } : i
                    );
                    onChangeCapex(updated);
                  }}
                  className="input-number"
                />
              </td>
              <td>
                <input
                  type="number"
                  value={item.depreciation_years}
                  min={1}
                  max={10}
                  onChange={(e) => {
                    const updated = data.capex.map(i =>
                      i.id === item.id ? { ...i, depreciation_years: parseInt(e.target.value) || 1 } : i
                    );
                    onChangeCapex(updated);
                  }}
                  className="input-number"
                />
                <span className="input-suffix">yrs</span>
              </td>
              <td>
                <button
                  className="btn-icon btn-delete"
                  onClick={() => onChangeCapex(data.capex.filter(i => i.id !== item.id))}
                  disabled={data.capex.length <= 1}
                >
                  ×
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h4>Operating Expenditure (OpEx)</h4>
      <table className="cost-table">
        <thead>
          <tr>
            <th>Item</th>
            <th>Monthly</th>
            <th>Duration</th>
            <th>Total</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {data.opex.map(item => (
            <tr key={item.id}>
              <td>
                <input
                  type="text"
                  value={item.item}
                  onChange={(e) => {
                    const updated = data.opex.map(i =>
                      i.id === item.id ? { ...i, item: e.target.value } : i
                    );
                    onChangeOpex(updated);
                  }}
                  className="input-text"
                />
              </td>
              <td>
                <input
                  type="number"
                  value={item.monthly_cost}
                  onChange={(e) => {
                    const updated = data.opex.map(i =>
                      i.id === item.id ? { ...i, monthly_cost: parseFloat(e.target.value) || 0 } : i
                    );
                    onChangeOpex(updated);
                  }}
                  className="input-number"
                />
              </td>
              <td>
                <input
                  type="number"
                  value={item.duration_months}
                  min={1}
                  max={60}
                  onChange={(e) => {
                    const updated = data.opex.map(i =>
                      i.id === item.id ? { ...i, duration_months: parseInt(e.target.value) || 1 } : i
                    );
                    onChangeOpex(updated);
                  }}
                  className="input-number"
                />
                <span className="input-suffix">mo</span>
              </td>
              <td className="total-cell">{formatCurrency(item.monthly_cost * item.duration_months)}</td>
              <td>
                <button
                  className="btn-icon btn-delete"
                  onClick={() => onChangeOpex(data.opex.filter(i => i.id !== item.id))}
                  disabled={data.opex.length <= 1}
                >
                  ×
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ============================================================================
// Line Item Section (for Training, Migration, Disruption)
// ============================================================================

interface LineItemSectionProps {
  items: LineItem[];
  onChange: (items: LineItem[]) => void;
}

const LineItemSection: React.FC<LineItemSectionProps> = ({ items, onChange }) => {
  const addItem = () => {
    const newItem: LineItem = {
      id: generateId(),
      item: 'New Item',
      quantity: 1,
      unit_cost: 0,
      total: 0,
      notes: '',
    };
    onChange([...items, newItem]);
  };

  return (
    <div className="line-item-section">
      <table className="cost-table">
        <thead>
          <tr>
            <th>Item</th>
            <th>Quantity</th>
            <th>Unit Cost</th>
            <th>Total</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.map(item => (
            <tr key={item.id}>
              <td>
                <input
                  type="text"
                  value={item.item}
                  onChange={(e) => {
                    const updated = items.map(i =>
                      i.id === item.id ? { ...i, item: e.target.value } : i
                    );
                    onChange(updated);
                  }}
                  className="input-text"
                />
              </td>
              <td>
                <input
                  type="number"
                  value={item.quantity}
                  min={0}
                  onChange={(e) => {
                    const updated = items.map(i =>
                      i.id === item.id ? { ...i, quantity: parseFloat(e.target.value) || 0 } : i
                    );
                    onChange(updated);
                  }}
                  className="input-number"
                />
              </td>
              <td>
                <input
                  type="number"
                  value={item.unit_cost}
                  min={0}
                  onChange={(e) => {
                    const updated = items.map(i =>
                      i.id === item.id ? { ...i, unit_cost: parseFloat(e.target.value) || 0 } : i
                    );
                    onChange(updated);
                  }}
                  className="input-number"
                />
              </td>
              <td className="total-cell">{formatCurrency(item.quantity * item.unit_cost)}</td>
              <td>
                <button
                  className="btn-icon btn-delete"
                  onClick={() => onChange(items.filter(i => i.id !== item.id))}
                  disabled={items.length <= 1}
                >
                  ×
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button className="btn btn-secondary btn-add" onClick={addItem}>
        + Add Item
      </button>
    </div>
  );
};

// ============================================================================
// Vendor Section
// ============================================================================

interface VendorSectionProps {
  items: VendorItem[];
  onChange: (items: VendorItem[]) => void;
}

const VendorSection: React.FC<VendorSectionProps> = ({ items, onChange }) => {
  return (
    <div className="vendor-section">
      <table className="cost-table">
        <thead>
          <tr>
            <th>Vendor/Service</th>
            <th>Duration</th>
            <th>Cost</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.map(item => (
            <tr key={item.id}>
              <td>
                <input
                  type="text"
                  value={item.vendor_type}
                  onChange={(e) => {
                    const updated = items.map(i =>
                      i.id === item.id ? { ...i, vendor_type: e.target.value } : i
                    );
                    onChange(updated);
                  }}
                  className="input-text"
                />
              </td>
              <td>
                <input
                  type="text"
                  value={item.duration}
                  onChange={(e) => {
                    const updated = items.map(i =>
                      i.id === item.id ? { ...i, duration: e.target.value } : i
                    );
                    onChange(updated);
                  }}
                  className="input-text input-duration"
                />
              </td>
              <td>
                <input
                  type="number"
                  value={item.cost}
                  min={0}
                  onChange={(e) => {
                    const updated = items.map(i =>
                      i.id === item.id ? { ...i, cost: parseFloat(e.target.value) || 0, total: parseFloat(e.target.value) || 0 } : i
                    );
                    onChange(updated);
                  }}
                  className="input-number"
                />
              </td>
              <td>
                <button
                  className="btn-icon btn-delete"
                  onClick={() => onChange(items.filter(i => i.id !== item.id))}
                  disabled={items.length <= 1}
                >
                  ×
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ============================================================================
// Compliance Section
// ============================================================================

interface ComplianceSectionProps {
  items: ComplianceItem[];
  onChange: (items: ComplianceItem[]) => void;
}

const ComplianceSection: React.FC<ComplianceSectionProps> = ({ items, onChange }) => {
  return (
    <div className="compliance-section">
      <table className="cost-table">
        <thead>
          <tr>
            <th>Item</th>
            <th>Phase</th>
            <th>Cost</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.map(item => (
            <tr key={item.id}>
              <td>
                <input
                  type="text"
                  value={item.item}
                  onChange={(e) => {
                    const updated = items.map(i =>
                      i.id === item.id ? { ...i, item: e.target.value } : i
                    );
                    onChange(updated);
                  }}
                  className="input-text"
                />
              </td>
              <td>
                <input
                  type="text"
                  value={item.phase}
                  onChange={(e) => {
                    const updated = items.map(i =>
                      i.id === item.id ? { ...i, phase: e.target.value } : i
                    );
                    onChange(updated);
                  }}
                  className="input-text input-phase"
                />
              </td>
              <td>
                <input
                  type="number"
                  value={item.cost}
                  min={0}
                  onChange={(e) => {
                    const updated = items.map(i =>
                      i.id === item.id ? { ...i, cost: parseFloat(e.target.value) || 0 } : i
                    );
                    onChange(updated);
                  }}
                  className="input-number"
                />
              </td>
              <td>
                <button
                  className="btn-icon btn-delete"
                  onClick={() => onChange(items.filter(i => i.id !== item.id))}
                  disabled={items.length <= 1}
                >
                  ×
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ============================================================================
// Risk Section
// ============================================================================

interface RiskSectionProps {
  data: { reserves: import('./costTypes').RiskReserve[] };
  onChange: (reserves: import('./costTypes').RiskReserve[]) => void;
}

const RiskSection: React.FC<RiskSectionProps> = ({ data, onChange }) => {
  return (
    <div className="risk-section">
      <table className="cost-table">
        <thead>
          <tr>
            <th>Reserve Type</th>
            <th>Basis</th>
            <th>%</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          {data.reserves.map((reserve, index) => (
            <tr key={reserve.reserve_type}>
              <td>{reserve.reserve_type}</td>
              <td className="basis-cell">{reserve.basis}</td>
              <td>
                {reserve.percentage > 0 && (
                  <input
                    type="number"
                    value={reserve.percentage}
                    min={0}
                    max={100}
                    onChange={(e) => {
                      const updated = data.reserves.map((r, i) =>
                        i === index
                          ? { ...r, percentage: parseFloat(e.target.value) || 0, user_override: true }
                          : r
                      );
                      onChange(updated);
                    }}
                    className="input-number input-percentage"
                  />
                )}
              </td>
              <td className="total-cell">{formatCurrency(reserve.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ============================================================================
// Export
// ============================================================================

export default CostConfigurationPanel;
